import { bearingDegrees, buildPolyline, corridorRing, destination, distanceMeters, orientedRectangle, ringCentroid, } from './geo.js';
import { at } from './at.js';
/** Cercle approché par un polygone régulier. */
export function circleRing(center, radiusM, segments = 32) {
    const ring = [];
    for (let i = 0; i < segments; i += 1) {
        ring.push(destination(center, (i * 360) / segments, radiusM));
    }
    ring.push({ ...at(ring, 0) });
    return ring;
}
function formatSlotCode(pattern, index) {
    return pattern.replace(/\{n(?::0(\d)\})?\}?/u, (_match, width) => width === undefined ? String(index) : String(index).padStart(Number(width), '0'));
}
/**
 * Places d'une rangée.
 *
 * Le pas le long de la rangée n'est PAS la largeur de la place quand les places
 * sont en épi : à un angle θ, deux places voisines se recouvrent partiellement
 * vues de la voie, et le pas vaut `largeur / sin θ`. Utiliser la largeur brute
 * tasserait les places et les ferait se chevaucher. À 90° — le cas de Bastia,
 * où les places sont droites — le sinus vaut 1 et le pas redevient la largeur.
 */
export function generateRow(axisFrom, axisTo, row) {
    const axisHeading = bearingDegrees(axisFrom, axisTo);
    const axisLength = distanceMeters(axisFrom, axisTo);
    const angleRad = (row.bay_angle_deg * Math.PI) / 180;
    const sinAngle = Math.sin(angleRad);
    const pitch = row.bay_pitch_m ?? (sinAngle < 0.05 ? row.bay_width_m : row.bay_width_m / sinAngle);
    // Les places sont centrées sur la rangée : le reliquat est réparti aux deux
    // extrémités plutôt que laissé en bout, ce qui décalerait toute la rangée.
    const used = (row.slot_count - 1) * pitch;
    const startOffset = Math.max(0, (axisLength - used) / 2) + (row.along_offset_m ?? 0);
    const perpendicular = (axisHeading + 90) % 360;
    const slots = [];
    for (let i = 0; i < row.slot_count; i += 1) {
        const angle = angleDeLaPlace(row, i + 1);
        // Côté quai comme côté digue, les épis s'ouvrent vers l'extérieur de la voie.
        const bayHeading = row.offset_m >= 0 ? (axisHeading + angle + 360) % 360 : (axisHeading - angle + 360) % 360;
        const width = row.bay_pitch_m === undefined
            ? row.bay_width_m
            : row.bay_pitch_m * Math.abs(Math.sin((angle * Math.PI) / 180));
        const alongAxis = destination(axisFrom, axisHeading, startOffset + i * pitch);
        const laneEdge = destination(alongAxis, perpendicular, row.offset_m);
        const center = destination(laneEdge, bayHeading, row.bay_length_m / 2);
        slots.push({
            areaCode: row.area_code,
            code: formatSlotCode(row.slot_code_pattern, i + 1),
            ring: orientedRectangle(center, bayHeading, row.bay_length_m, width),
            center,
            headingDeg: bayHeading,
        });
    }
    return slots;
}
/** Angle de la place `numero` (à partir de 1), selon le profil s'il existe. */
export function angleDeLaPlace(row, numero) {
    const profil = row.bay_angle_profile;
    if (!profil || profil.length === 0)
        return row.bay_angle_deg;
    const points = [...profil].sort((p, q) => p.slot - q.slot);
    const premier = at(points, 0);
    const dernier = at(points, points.length - 1);
    if (numero <= premier.slot)
        return premier.angle_deg;
    if (numero >= dernier.slot)
        return dernier.angle_deg;
    for (let k = 1; k < points.length; k += 1) {
        const avant = at(points, k - 1);
        const apres = at(points, k);
        if (numero <= apres.slot) {
            const t = (numero - avant.slot) / (apres.slot - avant.slot);
            return avant.angle_deg + t * (apres.angle_deg - avant.angle_deg);
        }
    }
    return dernier.angle_deg;
}
export function generateSite(spec) {
    const { north, south } = spec.mole_axis;
    const axis = buildPolyline([north, south]);
    const axisHeadingDeg = bearingDegrees(north, south);
    const zones = spec.zones.map((zone) => {
        let ring;
        if (zone.shape === 'corridor_along_axis') {
            ring = corridorRing(north, south, zone.half_width_m ?? spec.mole_axis.half_width_m);
        }
        else {
            if (!zone.center || zone.radius_m === undefined) {
                throw new Error(`Zone ${zone.code} : forme « circle » sans centre ni rayon.`);
            }
            ring = circleRing(zone.center, zone.radius_m);
        }
        return {
            code: zone.code,
            type: zone.type,
            label: zone.label,
            ring,
            center: zone.center ?? ringCentroid(ring),
            ...(zone.control_post_code !== undefined ? { controlPostCode: zone.control_post_code } : {}),
            confirmed: zone.confirmed ?? false,
            ...(zone.detection !== undefined ? { detection: zone.detection } : {}),
        };
    });
    const slots = spec.parking_rows.flatMap((row) => generateRow(north, south, row));
    const slotsByArea = new Map();
    for (const slot of slots) {
        const existing = slotsByArea.get(slot.areaCode);
        if (existing)
            existing.push(slot);
        else
            slotsByArea.set(slot.areaCode, [slot]);
    }
    return {
        axis,
        axisHeadingDeg,
        axisLengthM: axis.totalMeters,
        zones,
        slots,
        slotsByArea,
    };
}
//# sourceMappingURL=generate-zones.js.map